/** The activity category record — one category (`demo.Activity_Category`), read on the
 *  records core (plan 2026-10-04-console-activity-itinerary-records.md §4, §9.5, §9.7, §10.3;
 *  O8: a small record — a header, one card, an Activities panel).
 *
 *  The VIEW half reads two things, each through TanStack Query:
 *
 *  - `GET /activity_categories/{pk}/` — the detail (`activityCategories.retrieve`), under the
 *    key the old editor and every category write already use
 *    (`['activity_categories', 'detail', id]`);
 *  - `GET /activities/?category={pk}` — the "Activities in this category" panel's rows: the
 *    first page of the activities list filtered by the category (by name, the list's default
 *    order), under `['activities']` so an activity write refreshes it. The page asks for it
 *    only once the panel has been on screen.
 *
 *  The EDIT half (§9.7) reads and writes four more, the category changeset's:
 *
 *  - `GET /activity_categories/edit/catalog/` — the static registry: every field the record
 *    shows (label, card, widget), the effects (the category's app cache, the replaced icon's
 *    release) and the `ConsoleCatalogEditEnabled` kill switch (`enabled`). Cached for an
 *    hour (`useActivityCategoryEditCatalog`).
 *  - `GET /activity_categories/{pk}/edit_context/` — the per-category facts: `baselines`
 *    (every change's `from` — the icon as its Image id, which the detail does not carry),
 *    `blocked`, the caller's permissions, the kill switch (`flags`) and the tenant the icon
 *    and the access tags must belong to.
 *  - `POST /activity_categories/{pk}/changeset/preview/` — the plan of a changeset (the
 *    normalised diff, the conflicts, the effects); writes nothing.
 *  - `POST /activity_categories/{pk}/changeset/` — the apply, compare-and-set per field (a
 *    category has no timestamps: its values are the version): a 409 rejects as
 *    `CatalogDriftError`, a 400 (and the field-keyed 403) as
 *    `CatalogChangesetValidationError` — an icon the daily sweep removed is the field's
 *    `staged_image_gone` ("Upload it again"); the answer re-seeds the detail and the edit
 *    context.
 *
 *  A category takes no choice questions, fires no events and writes to no other system.
 *
 *  Vocabulary from the spec: `ActivityCategoryRecordFieldKey` (every key the record shows),
 *  `ActivityCategoryRecordEditableFieldKey` (the editable subset) and the catalog field's
 *  `section` (the one card) — the registry's contract test pins to the same enums. */
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import type { RecordApplyResult, RecordChangeset, RecordPreview } from '@/features/records/editor/types';

import * as activitiesApi from './activities';
import type { ActivityRow } from './activities';
import * as categoriesApi from './activityCategories';
import type { ActivityCategoryDetail, ActivityCategoryRow } from './activityCategories';
import { CatalogDriftError, toCatalogChangesetError } from './catalogRecord';
import { apiGet, apiPost } from './client';
import type { components } from './types';
import type { PaginatedResponse } from './users';

// ---------------------------------------------------------------------------
// Vocabulary
// ---------------------------------------------------------------------------

type CatalogField = components['schemas']['ActivityCategoryRecordCatalogField'];

/** Every field key the category record shows, editable or not, in card order. */
export type ActivityCategoryRecordFieldKey = components['schemas']['ActivityCategoryRecordFieldKey'];

/** The keys an `activity_category:<id>` change may carry. */
export type ActivityCategoryRecordEditableFieldKey =
  components['schemas']['ActivityCategoryRecordEditableFieldKey'];

/** The record's cards, in page order: its one card (the catalog field's `section`). */
export const ACTIVITY_CATEGORY_RECORD_SECTIONS = ['category'] as const satisfies readonly CatalogField['section'][];
export type ActivityCategoryRecordSection = (typeof ACTIVITY_CATEGORY_RECORD_SECTIONS)[number];

/** A catalog field's kind: `editable`, or `derived` (system, read-only). */
export type ActivityCategoryRecordFieldKind = CatalogField['kind'];

/** The edit-mode inputs the catalog names (null for a read-only field). */
export type ActivityCategoryRecordWidget = CatalogField['widget'];

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const activityCategoryDetailKey = (id: number) => ['activity_categories', 'detail', id] as const;
/** Under `['activities']`: an activity write (or the category's delete) refreshes it. */
export const categoryActivitiesKey = (id: number, first: number) =>
  ['activities', 'inCategory', id, first] as const;

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

export function useActivityCategoryDetail(
  id: number,
  opts: { retry?: (failures: number, error: unknown) => boolean } = {},
): UseQueryResult<ActivityCategoryDetail, unknown> {
  return useQuery<ActivityCategoryDetail, unknown>({
    queryKey: activityCategoryDetailKey(id),
    queryFn: () => categoriesApi.retrieve(id),
    ...(opts.retry ? { retry: opts.retry } : {}),
  });
}

/** The category's first `first` activities, by name, and how many it holds (`count`) —
 *  `enabled` only once the panel has been seen. */
export function useCategoryActivities(
  id: number,
  { enabled, first }: { enabled: boolean; first: number },
): UseQueryResult<PaginatedResponse<ActivityRow>, unknown> {
  return useQuery<PaginatedResponse<ActivityRow>, unknown>({
    queryKey: categoryActivitiesKey(id, first),
    queryFn: () => activitiesApi.list({ category: id, pageSize: first }),
    enabled,
  });
}

/** How many activities a category holds, from a categories grid page already in the cache
 *  (`['activity_categories', params]` — the grid the operator came from): what the record's
 *  loading skeleton is sized from before its detail arrives. Null when no cached page lists
 *  the category. */
export function cachedActivityCount(qc: QueryClient, id: number): number | null {
  for (const [, data] of qc.getQueriesData<unknown>({ queryKey: ['activity_categories'] })) {
    const rows = (data as { results?: unknown } | undefined)?.results;
    if (!Array.isArray(rows)) continue;
    const row = (rows as (Partial<ActivityCategoryRow> | null)[]).find((r) => r?.id === id);
    if (typeof row?.activity_count === 'number') return row.activity_count;
  }
  return null;
}

// ---------------------------------------------------------------------------
// EDIT half: the category changeset (§9.7)
// ---------------------------------------------------------------------------

type Schemas = components['schemas'];

export type ActivityCategoryRecordCatalog = Schemas['ActivityCategoryRecordCatalog'];
export type ActivityCategoryRecordCatalogField = CatalogField;
export type ActivityCategoryRecordEditContext = Schemas['ActivityCategoryRecordEditContext'];
export type ActivityCategoryRecordChange = Schemas['ActivityCategoryRecordChange'];
export type ActivityCategoryRecordChangeset = Schemas['ActivityCategoryRecordChangeset'];
export type ActivityCategoryRecordPreview = Schemas['ActivityCategoryRecordPreview'];
/** The apply's answer, its `detail` in the shape the record reads (`activityCategories.retrieve`). */
export type ActivityCategoryRecordApplyResult = Omit<Schemas['ActivityCategoryRecordApplyResult'], 'detail'> & {
  detail: ActivityCategoryDetail | null;
};

/** The change entity of the category record. */
export const ACTIVITY_CATEGORY_CHANGE_ENTITY = 'activity_category';

/** The records core's changeset as this endpoint takes it: the category's field changes and
 *  the description. Never `options` — a category asks no question, and any key is a 400. */
export function toActivityCategoryChangeset(body: RecordChangeset): ActivityCategoryRecordChangeset {
  const out: ActivityCategoryRecordChangeset = {
    changes: body.changes.flatMap((c) =>
      c.entity === ACTIVITY_CATEGORY_CHANGE_ENTITY && c.field
        ? [
            {
              entity: ACTIVITY_CATEGORY_CHANGE_ENTITY,
              id: c.id,
              field: c.field as ActivityCategoryRecordChange['field'],
              from: c.from,
              to: c.to,
            },
          ]
        : [],
    ),
  };
  if (body.description) out.description = body.description;
  return out;
}

/** A category plan as the ledger reads it: no events, nothing written to another system —
 *  its effects (the app cache, the replaced icon) are the save's own. */
export function toActivityCategoryRecordPreview(p: ActivityCategoryRecordPreview): RecordPreview {
  return { ...p, events: [], external: [], follow_ups: [] };
}

/** An apply result as the ledger reads it (no events, no event tag). */
export function toActivityCategoryApplyResult(r: ActivityCategoryRecordApplyResult): RecordApplyResult {
  return {
    audit_id: r.audit_id,
    audit_uuid: r.audit_uuid,
    event_tag: null,
    saved_ts: r.saved_ts,
    changes: r.changes,
    events: [],
    follow_ups: r.follow_ups,
    warnings: r.warnings,
  };
}

// ---- calls -----------------------------------------------------------------

export function fetchActivityCategoryEditCatalog(): Promise<ActivityCategoryRecordCatalog> {
  return apiGet<ActivityCategoryRecordCatalog>('/activity_categories/edit/catalog/');
}

export function fetchActivityCategoryEditContext(
  id: number,
  signal?: AbortSignal,
): Promise<ActivityCategoryRecordEditContext> {
  return apiGet<ActivityCategoryRecordEditContext>(
    `/activity_categories/${id}/edit_context/`,
    signal ? { signal } : undefined,
  );
}

export async function previewActivityCategoryChangeset(
  id: number,
  body: ActivityCategoryRecordChangeset,
  signal?: AbortSignal,
): Promise<ActivityCategoryRecordPreview> {
  try {
    return await apiPost<ActivityCategoryRecordPreview>(
      `/activity_categories/${id}/changeset/preview/`,
      body,
      signal ? { signal } : undefined,
    );
  } catch (e) {
    throw toCatalogChangesetError(e);
  }
}

export async function applyActivityCategoryChangeset(
  id: number,
  body: ActivityCategoryRecordChangeset,
): Promise<ActivityCategoryRecordApplyResult> {
  try {
    return await apiPost<ActivityCategoryRecordApplyResult>(`/activity_categories/${id}/changeset/`, body);
  } catch (e) {
    throw toCatalogChangesetError(e);
  }
}

// ---- React Query -----------------------------------------------------------

/** The catalog changes only with a deploy. */
export const ACTIVITY_CATEGORY_EDIT_CATALOG_STALE_MS = 60 * 60_000;

export const activityCategoryRecordKeys = {
  catalog: () => ['activityCategoryRecord', 'catalog'] as const,
  editContext: (id: number) => ['activityCategoryRecord', 'editContext', id] as const,
  preview: (id: number, body: ActivityCategoryRecordChangeset) =>
    ['activityCategoryRecord', 'preview', id, body] as const,
};

export function useActivityCategoryEditCatalog(): UseQueryResult<ActivityCategoryRecordCatalog> {
  return useQuery({
    queryKey: activityCategoryRecordKeys.catalog(),
    queryFn: fetchActivityCategoryEditCatalog,
    staleTime: ACTIVITY_CATEGORY_EDIT_CATALOG_STALE_MS,
  });
}

export function useActivityCategoryEditContext(
  id: number,
  { enabled = true }: { enabled?: boolean } = {},
): UseQueryResult<ActivityCategoryRecordEditContext> {
  return useQuery({
    queryKey: activityCategoryRecordKeys.editContext(id),
    queryFn: ({ signal }) => fetchActivityCategoryEditContext(id, signal),
    enabled: enabled && Number.isFinite(id),
  });
}

/** The ledger's preview of a (debounced) compiled draft; `null` = nothing to preview —
 *  keyed by the body, the previous plan kept on screen while the next loads. */
export function useActivityCategoryChangesetPreview(
  id: number,
  body: RecordChangeset | null,
): UseQueryResult<RecordPreview> {
  const wire = body ? toActivityCategoryChangeset(body) : { changes: [] };
  return useQuery({
    queryKey: activityCategoryRecordKeys.preview(id, wire),
    queryFn: ({ signal }) => previewActivityCategoryChangeset(id, wire, signal),
    select: toActivityCategoryRecordPreview,
    enabled: body !== null && wire.changes.length > 0 && Number.isFinite(id),
    staleTime: 0,
    gcTime: 60_000,
    retry: false,
    placeholderData: keepPreviousData,
  });
}

/** Save. `onSaved` runs first (the draft lets go of what the save stored before the
 *  re-seeded baselines rebase it), then the edit context and the detail are re-seeded from
 *  the answer — refetched instead when the server could not re-read them — and the
 *  categories grids refresh, as do the activities' reads (an activity shows its category's
 *  title and icon); never the used-by reads, which a category edit cannot change. A 409
 *  rejects with `CatalogDriftError` (the edit context is refetched for the rebase), a 400 /
 *  field-keyed 403 with `CatalogChangesetValidationError`. */
export function useApplyActivityCategoryChangeset(
  id: number,
  { onSaved }: { onSaved?: (result: RecordApplyResult) => void } = {},
): UseMutationResult<ActivityCategoryRecordApplyResult, unknown, RecordChangeset> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: RecordChangeset) => applyActivityCategoryChangeset(id, toActivityCategoryChangeset(body)),
    onSuccess: (result) => {
      onSaved?.(toActivityCategoryApplyResult(result));
      if (result.edit_context) qc.setQueryData(activityCategoryRecordKeys.editContext(id), result.edit_context);
      else void qc.invalidateQueries({ queryKey: activityCategoryRecordKeys.editContext(id) });
      if (result.detail) qc.setQueryData(activityCategoryDetailKey(id), result.detail);
      // The grids — and the detail itself when the answer could not re-read it.
      void qc.invalidateQueries({
        queryKey: ['activity_categories'],
        predicate: ({ queryKey: [, kind, key] }) => !(result.detail && kind === 'detail' && key === id),
      });
      void qc.invalidateQueries({
        queryKey: ['activities'],
        predicate: ({ queryKey: [, kind] }) => kind !== 'usedBy' && kind !== 'usedBySummary',
      });
    },
    onError: (e) => {
      if (e instanceof CatalogDriftError) {
        void qc.invalidateQueries({ queryKey: activityCategoryRecordKeys.editContext(id) });
      }
    },
  });
}
